import { useEffect, useState } from 'react'
import { URL_SUPABASE } from '../../api/supabase'
import {
  ajouterBoitier,
  etatSourceGps,
  modifierBoitier,
  regenererSecretGps,
  supprimerBoitier,
} from '../../api/boitiers'
import { depuis, etatPosition } from '../../api/geo'
import { blocTraccar, identifiantValide, libelleRattachement, nomBoitier, secretMasque, urlWebhook } from '../../api/gps'
import type { Boitier, DonneesOps } from '../../api/types'
import { useDroits } from '../Contexte'
import { Icone } from '../Icone'
import { ListeDeroulante } from '../ListeDeroulante'
import { Modale } from '../Modale'
import { FicheBoitier } from './FicheBoitier'

/**
 * Boîtiers GPS (migration 0026) — le suivi des véhicules par boîtier, via Traccar.
 *
 * Traccar tourne en dessous et POUSSE chaque position vers SwiftDeliver : cet écran
 * ne l'appelle jamais directement. Il sert à trois choses — brancher Traccar (le
 * secret), déclarer quel boîtier est sur quel véhicule ou livreur, et ouvrir la FICHE
 * d'un boîtier (0052) : son nom (aligné sur Traccar), ses commandes et leur journal.
 *
 * ⚠️ Cet écran avait été retiré de la navigation le 06/10/2026 ; il y revient (voir
 * `Navigation.tsx`) : l'opératrice a ses propres boîtiers à nommer et à commander.
 *
 * Le secret n'est affiché qu'UNE SEULE FOIS, à sa génération : la base n'en garde
 * que l'empreinte, rien ne peut le redonner. Perdu = on en génère un nouveau (et
 * l'ancien cesse de fonctionner).
 */
export function EcranBoitiers({
  donnees,
  orgId,
  surRafraichir,
  surNotifier,
}: {
  donnees: DonneesOps
  orgId: string
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
}) {
  const droits = useDroits()
  const gere = droits.peut('boitiers') && !droits.apercu
  const [modaleAjout, setModaleAjout] = useState(false)
  const [aRattacher, setARattacher] = useState<Boitier | null>(null)
  const [fiche, setFiche] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [source, setSource] = useState<{ configuree: boolean; miseAJour: string | null } | null | 'inconnu'>('inconnu')
  const [enCours, setEnCours] = useState(false)
  const [maintenant, setMaintenant] = useState(() => Date.now())

  useEffect(() => {
    const t = setInterval(() => setMaintenant(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (droits.apercu) return
    let vivant = true
    void etatSourceGps(orgId).then((e) => vivant && setSource(e))
    return () => {
      vivant = false
    }
  }, [orgId, droits.apercu])

  async function genererSecret() {
    const deja = source !== 'inconnu' && source?.configuree
    if (
      deja &&
      !window.confirm(
        'Régénérer le secret ?\n\nL’ancien cessera de fonctionner immédiatement : Traccar ne sera plus accepté tant que vous n’aurez pas collé le nouveau dans traccar.xml et redémarré le conteneur.',
      )
    )
      return
    setEnCours(true)
    try {
      setSecret(await regenererSecretGps(orgId))
      setSource({ configuree: true, miseAJour: new Date().toISOString() })
    } catch (e) {
      surNotifier(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
    }
  }

  async function basculer(b: Boitier) {
    try {
      await modifierBoitier(b.id, { active: !b.active })
      surNotifier(b.active ? 'Boîtier désactivé : ses positions sont refusées' : 'Boîtier activé')
      await surRafraichir()
    } catch (e) {
      surNotifier(e instanceof Error ? e.message : String(e))
    }
  }

  async function retirer(b: Boitier) {
    if (!window.confirm(`Supprimer le boîtier ${nomBoitier(b, donnees.vehicules)} ?\n\nSon historique de positions est supprimé avec lui.`)) return
    try {
      await supprimerBoitier(b.id)
      surNotifier('Boîtier supprimé')
      await surRafraichir()
    } catch (e) {
      surNotifier(e instanceof Error ? e.message : String(e))
    }
  }

  /* La fiche relit le boîtier à chaque rendu : le nom enregistré (ou une position reçue en direct) s'y reflète tout de suite. */
  const boitierFiche = fiche ? donnees.boitiers.find((x) => x.id === fiche) : undefined

  return (
    <>
      {!donnees.synchro.boitiers && (
        <div className="bandeau alerte">
          Le suivi par boîtier n’est pas encore activé sur la base (la migration <span className="mono">0026</span> n’est pas appliquée). Les boîtiers ne
          peuvent ni être déclarés ni afficher de position.
        </div>
      )}

      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3>
              <Icone nom="pin" classe="h-ic" /> Connexion à Traccar
            </h3>
            <div className="ch-sub">
              Traccar reçoit les boîtiers et pousse leurs positions ici. Rien n’est appelé dans l’autre sens : Traccar n’a pas besoin d’être accessible depuis
              Internet.
            </div>
          </div>
          <span className="sp" />
          {gere && (
            <button className="btn btn-gold btn-sm" onClick={() => void genererSecret()} disabled={enCours || !donnees.synchro.boitiers}>
              {source !== 'inconnu' && source?.configuree ? 'Régénérer le secret' : 'Générer le secret'}
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="field">
            <label>Adresse à renseigner dans Traccar (forward.url)</label>
            <input readOnly value={urlWebhook(URL_SUPABASE)} onFocus={(e) => e.currentTarget.select()} />
          </div>
          <div className="t-muted" style={{ fontSize: 13 }}>
            {source === 'inconnu' || source === null
              ? 'État du secret indisponible.'
              : source.configuree
                ? `Secret en place (${secretMasque()}), généré le ${source.miseAJour ? new Date(source.miseAJour).toLocaleString('fr-FR') : '—'}. Il n’est plus affichable : en cas de perte, régénérez-le.`
                : 'Aucun secret généré : Traccar sera refusé tant qu’il n’y en a pas.'}
          </div>
          {!droits.peut('boitiers') && <p className="t-muted">{droits.refus}</p>}
        </div>
      </div>

      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3>
              <Icone nom="dispatch" classe="h-ic" /> Boîtiers
            </h3>
            <div className="ch-sub">
              {donnees.boitiers.length} boîtier{donnees.boitiers.length > 1 ? 's' : ''} · leur position apparaît sur la carte du dispatch
            </div>
          </div>
          <span className="sp" />
          {gere && (
            <button className="btn btn-gold btn-sm" onClick={() => setModaleAjout(true)} disabled={!donnees.synchro.boitiers}>
              <Icone nom="plus" /> Ajouter un boîtier
            </button>
          )}
        </div>
        <div className="card-body flush">
          {donnees.boitiers.length === 0 ? (
            <div className="no-hit" style={{ padding: 18 }}>
              Aucun boîtier. Ajoutez-en un avec l’identifiant qu’il porte dans Traccar (souvent son IMEI).
            </div>
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Boîtier</th>
                    <th>Rattaché à</th>
                    <th>Dernière position</th>
                    <th>Vitesse</th>
                    <th>Statut</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {donnees.boitiers.map((b) => {
                    const pos = etatPosition({ lat: b.last_lat, lng: b.last_lng, last_seen: b.last_fix_at }, maintenant)
                    return (
                      <tr key={b.id}>
                        <td>
                          <div className="t-strong">{nomBoitier(b, donnees.vehicules)}</div>
                          <div className="t-muted mono">{b.unique_id}</div>
                        </td>
                        <td>{libelleRattachement(b, donnees.vehicules, donnees.drivers)}</td>
                        <td className="t-muted">
                          {pos.etat === 'aucune' ? (
                            'Aucune position reçue'
                          ) : (
                            <>
                              {pos.etat === 'recente' ? 'En direct' : <b>Ancienne</b>} · {depuis(pos.ageMs)}
                            </>
                          )}
                        </td>
                        <td className="mono">{b.last_speed_kmh != null ? `${Math.round(b.last_speed_kmh)} km/h` : '—'}</td>
                        <td>
                          <span className={`badge ${b.active ? 'b-green' : 'b-gray'}`}>{b.active ? 'Actif' : 'Désactivé'}</span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-gold btn-sm" onClick={() => setFiche(b.id)} title="Nom du boîtier, commandes Traccar, journal">
                            Fiche
                          </button>
                          {gere && (
                            <>
                              {' '}
                              <button className="btn btn-outline btn-sm" onClick={() => setARattacher(b)}>
                                Rattacher
                              </button>{' '}
                              <button className="btn btn-outline btn-sm" onClick={() => void basculer(b)}>
                                {b.active ? 'Désactiver' : 'Activer'}
                              </button>{' '}
                              <button
                                className="btn btn-outline btn-sm"
                                onClick={() => void retirer(b)}
                                style={{ color: 'var(--err)' }}
                                aria-label={`Supprimer le boîtier ${nomBoitier(b, donnees.vehicules)}`}
                                title="Supprimer"
                              >
                                <Icone nom="x" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modaleAjout && (
        <ModaleAjout
          orgId={orgId}
          donnees={donnees}
          surFermer={() => setModaleAjout(false)}
          surNotifier={surNotifier}
          surAjoute={async () => {
            setModaleAjout(false)
            surNotifier('Boîtier ajouté')
            await surRafraichir()
          }}
        />
      )}

      {aRattacher && (
        <ModaleRattacher
          boitier={aRattacher}
          donnees={donnees}
          surFermer={() => setARattacher(null)}
          surNotifier={surNotifier}
          surRattache={async () => {
            setARattacher(null)
            surNotifier('Boîtier mis à jour')
            await surRafraichir()
          }}
        />
      )}

      {boitierFiche && (
        <FicheBoitier
          boitier={boitierFiche}
          donnees={donnees}
          surFermer={() => setFiche(null)}
          surRafraichir={surRafraichir}
          surNotifier={surNotifier}
        />
      )}

      {secret && (
        <Modale titre="Secret de connexion à Traccar" ouverte surFermer={() => setSecret(null)} surEnregistrer={() => setSecret(null)} libelleEnregistrer="J’ai copié le secret">
          <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            <div className="bandeau alerte" style={{ marginBottom: 12 }}>
              <b>Ce secret n’est affiché qu’une seule fois.</b> SwiftDeliver n’en garde que l’empreinte : une fois cette fenêtre fermée, personne ne peut le
              relire — il faudra en générer un nouveau.
            </div>
            <div className="field">
              <label>Secret</label>
              <input readOnly value={secret} onFocus={(e) => e.currentTarget.select()} className="mono" />
            </div>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => {
                void navigator.clipboard?.writeText(secret).then(
                  () => surNotifier('Secret copié'),
                  () => surNotifier('Copie impossible : sélectionnez le texte et copiez-le à la main'),
                )
              }}
            >
              Copier le secret
            </button>
            <div style={{ margin: '14px 0 6px', fontWeight: 600 }}>À ajouter dans traccar.xml (puis redémarrer le conteneur Traccar)</div>
            <pre
              style={{
                background: 'var(--gold-wash)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 12px',
                fontSize: 12,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {blocTraccar(secret, URL_SUPABASE)}
            </pre>
          </div>
        </Modale>
      )}
    </>
  )
}

function ModaleAjout({
  orgId,
  donnees,
  surFermer,
  surNotifier,
  surAjoute,
}: {
  orgId: string
  donnees: DonneesOps
  surFermer: () => void
  surNotifier: (message: string) => void
  surAjoute: () => Promise<void>
}) {
  const [uniqueId, setUniqueId] = useState('')
  const [label, setLabel] = useState('')
  const [vehicule, setVehicule] = useState('')
  const [livreur, setLivreur] = useState('')
  const [enCours, setEnCours] = useState(false)

  async function enregistrer() {
    const id = identifiantValide(uniqueId)
    if (!id) {
      surNotifier('Identifiant du boîtier obligatoire (64 caractères au plus)')
      return
    }
    if (!vehicule && !livreur) {
      surNotifier('Rattachez le boîtier à un véhicule ou à un livreur')
      return
    }
    setEnCours(true)
    try {
      await ajouterBoitier(orgId, { unique_id: id, label, vehicle_id: vehicule || null, driver_id: livreur || null })
      await surAjoute()
    } catch (e) {
      surNotifier(e instanceof Error ? e.message : String(e))
      setEnCours(false)
    }
  }

  return (
    <Modale titre="Ajouter un boîtier GPS" ouverte surFermer={surFermer} surEnregistrer={() => void enregistrer()} enCours={enCours}>
      <div className="field">
        <label>Identifiant dans Traccar (uniqueId — souvent l’IMEI)</label>
        <input value={uniqueId} onChange={(e) => setUniqueId(e.target.value)} placeholder="ex. 358765432109876" autoComplete="off" />
      </div>
      <div className="field">
        <label>Nom (facultatif)</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="ex. Moto livraison 1" />
      </div>
      <div className="field">
        <label>Véhicule</label>
        <ListeDeroulante
          pleineLargeur
          valeur={vehicule}
          surChangement={setVehicule}
          options={[
            { valeur: '', libelle: '— aucun —' },
            ...donnees.vehicules.map((v) => ({ valeur: v.id, libelle: [v.plate, v.model].filter(Boolean).join(' · ') || 'Véhicule' })),
          ]}
        />
      </div>
      <div className="field">
        <label>Livreur</label>
        <ListeDeroulante
          pleineLargeur
          valeur={livreur}
          surChangement={setLivreur}
          options={[{ valeur: '', libelle: '— aucun —' }, ...donnees.drivers.map((d) => ({ valeur: d.id, libelle: d.full_name }))]}
        />
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', background: 'var(--gold-wash)', borderRadius: 8, padding: '8px 10px' }}>
        Rattaché à un <b>livreur</b>, le boîtier alimente SA position (la même que celle de son téléphone : la plus récente l’emporte, la carte indique la
        source). Rattaché à un <b>véhicule</b> seul, il apparaît sur la carte comme un carré étiqueté de la plaque.
      </div>
    </Modale>
  )
}

/**
 * Rattache un boîtier à un véhicule et/ou un livreur de l'organisation (la base vérifie). Le NOM ne se change pas ici : il se change
 * dans la fiche du boîtier, où il est aligné sur Traccar.
 */
function ModaleRattacher({
  boitier,
  donnees,
  surFermer,
  surNotifier,
  surRattache,
}: {
  boitier: Boitier
  donnees: DonneesOps
  surFermer: () => void
  surNotifier: (message: string) => void
  surRattache: () => Promise<void>
}) {
  const [vehicule, setVehicule] = useState(boitier.vehicle_id ?? '')
  const [livreur, setLivreur] = useState(boitier.driver_id ?? '')
  const [enCours, setEnCours] = useState(false)

  async function enregistrer() {
    if (!vehicule && !livreur) {
      surNotifier('Rattachez le boîtier à un véhicule ou à un livreur')
      return
    }
    setEnCours(true)
    try {
      await modifierBoitier(boitier.id, { vehicle_id: vehicule || null, driver_id: livreur || null })
      await surRattache()
    } catch (e) {
      surNotifier(e instanceof Error ? e.message : String(e))
      setEnCours(false)
    }
  }

  return (
    <Modale titre={`Rattacher le boîtier ${nomBoitier(boitier, donnees.vehicules)}`} ouverte surFermer={surFermer} surEnregistrer={() => void enregistrer()} enCours={enCours}>
      <div className="field">
        <label>Véhicule</label>
        <ListeDeroulante
          pleineLargeur
          valeur={vehicule}
          surChangement={setVehicule}
          options={[
            { valeur: '', libelle: '— aucun —' },
            ...donnees.vehicules.map((v) => ({ valeur: v.id, libelle: [v.plate, v.model].filter(Boolean).join(' · ') || 'Véhicule' })),
          ]}
        />
      </div>
      <div className="field">
        <label>Livreur</label>
        <ListeDeroulante
          pleineLargeur
          valeur={livreur}
          surChangement={setLivreur}
          options={[{ valeur: '', libelle: '— aucun —' }, ...donnees.drivers.map((d) => ({ valeur: d.id, libelle: d.full_name }))]}
        />
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', background: 'var(--gold-wash)', borderRadius: 8, padding: '8px 10px' }}>
        Pour changer le <b>nom</b> du boîtier, ouvrez sa <b>fiche</b> : le nom y est enregistré à l’identique dans Traccar.
      </div>
    </Modale>
  )
}
